import { useCallback, useEffect, useMemo } from "react"
import { get, isNil } from "lodash"
import { useTheme, Box } from "@mui/material"
import { useSearchParams } from "react-router-dom"

import { GTM_AMENITY_SEARCH } from "constant"
import { pushDataLayer } from "utils/googletagmanager"

import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { useGetMapViewFromUrl } from "hooks/map"
import { useIndoorMap } from "providers/venue/modules/indoormap/hooks/useIndoorMap"
import { useFeatureCategory } from "hooks/feature"
import { useGeoLocation } from "providers/geolocation"
import { useAppUI } from "providers/ui"

import { AppNavigation } from "containers/AppNavigation"
import { SearchToggleButton } from "components/domains/search/SearchToggleButton"
import { FeatureChipToggleBar } from "components/domains/features/ChipToggleBar"
import { useMap } from "hooks/map/useMap"
import {
  Feature,
  useHighLightFeaturesMap,
} from "hooks/feature/useHighLightFeaturesMap"
import { OrdinalSelector } from "extensions/iconsiam/components/ordinal-selector"
import AttractionButton from "extensions/iconsiam/components/domains/attractions/MobileAttractionButton"

export const FeatureByCategory = () => {
  const theme = useTheme()
  const { locationVenue, locationOrdinal } = useGeoLocation()
  const locationVenueId = get(locationVenue, "id")
  const [searchParams, setSearchParams] = useSearchParams()
  const { isMultiOrdinalVenue, dataLoaded } = useVenue()
  const { openSearch } = useAppUI()
  const { t } = useTranslation()
  const { sortedLevels } = useMap()
  const {
    ordinalSelector,
    quickLinkContainer, //TODO:: planning about quick search's working flow later
    searchBarContainer,
    attractionButton,
    navigationContainer,
  } = theme.components[`AppMap`]?.styleOverrides || {}

  const {
    viewingCategory,
    viewingVenueId,
    viewingOrdinal,
    comingFromOrdinal,
    featureCategories,
    searchableFeatures,
    handleClickQuickSearch,
  } = useFeatureCategory({
    initialVenueId: locationVenueId,
    initialOrdinal: locationOrdinal,
  })

  const enableMap =
    !isNil(viewingOrdinal) && isNil(comingFromOrdinal) && dataLoaded

  const { mapView } = useGetMapViewFromUrl()

  const { flyTo } = useIndoorMap()

  useEffect(() => {
    flyTo(mapView)
  }, [flyTo, mapView])

  const onClickOrdinal = useCallback(
    (ordinal) => {
      const currentParams = Object.fromEntries(searchParams.entries())
      setSearchParams({ ...currentParams, ordinal })
    },
    [setSearchParams, searchParams]
  )

  const parsedOrdinal = useMemo(() => Number(viewingOrdinal), [viewingOrdinal])

  useHighLightFeaturesMap(searchableFeatures as Feature[], parsedOrdinal, {
    enabled: enableMap,
  })

  useEffect(() => {
    if (viewingCategory) {
      pushDataLayer({
        event: GTM_AMENITY_SEARCH,
        amenity_category: viewingCategory,
      })
    }
  }, [viewingCategory])

  return (
    <>
      <Box sx={searchBarContainer}>
        <SearchToggleButton
          placeholder={t("Search for your destination")}
          onClick={openSearch}
        />
      </Box>

      <Box sx={quickLinkContainer}>
        <FeatureChipToggleBar
          categories={featureCategories}
          onClick={handleClickQuickSearch}
        />
      </Box>
      <Box sx={attractionButton}>
        <AttractionButton />
      </Box>
      {isMultiOrdinalVenue && (
        <Box sx={ordinalSelector}>
          <OrdinalSelector
            selectedOrdinal={parsedOrdinal}
            levels={sortedLevels}
            onClickOrdinal={onClickOrdinal}
            currentVenue={locationVenueId}
            viewingVenueId={viewingVenueId}
            locationOrdinal={locationOrdinal}
          />
        </Box>
      )}
      <Box sx={navigationContainer}>
        <AppNavigation />
      </Box>
    </>
  )
}
